<template>
  <header>
    <a-form class="form" layout="vertical" ref="formRef" :model="formState">
      <a-form-item name="dates" required>
        <template #label>
          Selecciona el rango de fechas
        </template>

        <plan-range-picker
          v-model="formState.dates"
          :config-key="RangeConfigKey.MessagingSearchRange"
          :default-limit="DateRangeLimit"
        />
      </a-form-item>
    </a-form>

    <div class="actions">
      <span v-if="totalRecords !== undefined" class="total">{{ totalRecords }} Registros</span>

      <a-tooltip placement="top">
        <template #title>
          <span v-if="isGlobalDownloading">Tiene una descarga pendiente</span>
          <span v-else>
            Se descargarán únicamente los datos de la tabla de acuerdo a los filtros seleccionados.
          </span>
        </template>

        <a-button
          type="link"
          :disabled="isButtonDisabled || isGlobalDownloading"
          :loading="isDownloading"
          @click="handleDownload"
        >
          Descargar
          <DownloadOutlined />
        </a-button>
      </a-tooltip>

      <a-button type="primary" :disabled="isButtonDisabled || loadingSearch" :loading="loadingSearch" @click="handleSearch">
        Buscar
        <SearchOutlined />
      </a-button>
    </div>
  </header>

  <a-table
    size="middle"
    id="inbound-table"
    :scroll="scrollStyle"
    :columns="callsColumns"
    :data-source="reportData?.results"
    :loading="isTableLoading"
    :pagination="pagination"
    @change="handleTableChange"
  />
</template>

<script setup lang="ts">
import { computed, reactive, ref } from "vue";
import { DownloadOutlined, SearchOutlined } from "@ant-design/icons-vue";

import type { Form, Report } from "@/@types/reports/calls";
import { useScrollResize } from "@/app/shared/composables/useScrollResize";
import { TABLE_PAGE_SIZE } from "@/app/shared/utils/constants";
import { callsReportService } from "@/app/reports/services/CallsReportService";
import { callsColumns } from "@/app/reports/utils/tableColumns/calls";
import { useGlobalDownloadState } from "@/app/shared/composables/useGlobalDownloadState";
import { RangeConfigKey } from "@/app/shared/utils/planDatePicker";

import PlanRangePicker from "@/app/shared/components/pickers/PlanRangePicker.vue";

const { scrollStyle } = useScrollResize({ headerMargin: 420, width: 2328 });
const { isGlobalDownloading } = useGlobalDownloadState();

// Form
const formState: Form = reactive({
  dates: null,
});
const totalRecords = ref<number>();
const DateRangeLimit = 31;
const isButtonDisabled = computed(() => !formState.dates || formState.dates.some((date) => !date));
const isDownloading = ref(false);

// Table
const reportData = ref<Report>();
const currentTablePage = ref(1);
const isTableLoading = ref(false);
const loadingSearch = ref(false);

const pagination = computed(() => ({
  total: reportData.value?.count,
  current: currentTablePage.value,
  pageSize: TABLE_PAGE_SIZE,
  showSizeChanger: false,
}));

const handleTableChange = async (page: { pageSize: number; current: number }) => {
  currentTablePage.value = page.current;
  await fetchReportData();
};

const fetchReportData = async () => {
  isTableLoading.value = true;

  const response = await callsReportService.getList(formState.dates, currentTablePage.value);
  reportData.value = response;
  totalRecords.value = reportData.value?.count ?? 0;

  isTableLoading.value = false;
};

const handleSearch = async () => {
  if (loadingSearch.value) return;

  try {
    loadingSearch.value = true;
    currentTablePage.value = 1;
    await fetchReportData();
  } catch (error) {
    console.log("Error handleSearch Calls", error);
  } finally {
    loadingSearch.value = false;
  }
};

const handleDownload = async () => {
  isDownloading.value = true;
  await callsReportService.download(formState.dates);
  isDownloading.value = false;
};
</script>

<style lang="sass" scoped>
header
  display: flex
  justify-content: space-between
  align-items: flex-end
  margin: 15px 0

.form
  display: flex
  gap: 10px

.ant-form-item
  margin: 0
  width: 320px

:deep(.ant-form-item-label)
  padding: 0

.actions
  display: flex
  align-items: center
  gap: 10px

#inbound-table tfoot th,
#inbound-table tfoot td
  background-color: #fafafa !important
</style>
